import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CalendarDays, Plus, Copy, Users, Trash2, Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BLANK = {
  name: '', program_type: 'summer_program', description: '', starts_at: '', ends_at: '',
  age_min: '', age_max: '', capacity: '', location: '', invite_code: '', status: 'open',
};
const err = (e, f) => e?.response?.data?.detail?.message || e?.response?.data?.detail || f;

/** Programs are what a young person actually joins: a session, a cohort, a class period. */
export const ProgramsPanel = ({ orgId, slug, accent = '#22d3ee', canManage, onChanged }) => {
  const { getAuthHeader } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/orgs/${orgId}/programs`, getAuthHeader())
      .then(r => { setPrograms(r.data.programs); setTypes(r.data.types); })
      .catch(() => {});
  }, [orgId, getAuthHeader]);
  useEffect(load, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f };
      ['age_min', 'age_max', 'capacity'].forEach(k => {
        payload[k] = payload[k] === '' ? null : Number(payload[k]);
      });
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      const r = editing
        ? await axios.put(`${API}/orgs/${orgId}/programs/${editing}`, payload, getAuthHeader())
        : await axios.post(`${API}/orgs/${orgId}/programs`, payload, getAuthHeader());
      toast.success(r.data.join_instructions || 'Program saved');
      setOpen(false); setEditing(null); setF(BLANK);
      load(); onChanged?.();
    } catch (e) { toast.error(err(e, 'Could not save that program')); }
    finally { setSaving(false); }
  };

  const archive = async (id) => {
    try {
      await axios.delete(`${API}/orgs/${orgId}/programs/${id}`, getAuthHeader());
      toast.success('Program archived'); load(); onChanged?.();
    } catch (e) { toast.error(err(e, 'Could not archive that')); }
  };

  const edit = (p) => {
    setEditing(p.id);
    setF({
      name: p.name || '', program_type: p.program_type || 'other', description: p.description || '',
      starts_at: (p.starts_at || '').slice(0, 10), ends_at: (p.ends_at || '').slice(0, 10),
      age_min: p.age_min ?? '', age_max: p.age_max ?? '', capacity: p.capacity ?? '',
      location: p.location || '', invite_code: p.invite_code || '', status: p.status || 'open',
    });
    setOpen(true);
  };

  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-slate-900/60 p-5 md:p-6"
      data-testid="programs-panel">
      <div className="flex flex-wrap items-center gap-3">
        <CalendarDays className="w-4 h-4" style={{ color: accent }} />
        <h2 className="font-outfit text-xl font-semibold text-white">Programs</h2>
        <span className="text-xs text-slate-300">{programs.length} set up</span>
        {canManage && (
          <Button onClick={() => { setEditing(null); setF(BLANK); setOpen(true); }}
            data-testid="new-program-btn"
            className="ml-auto rounded-full text-slate-900 font-semibold"
            style={{ background: accent }}>
            <Plus className="w-4 h-4 mr-1.5" /> New program
          </Button>
        )}
      </div>
      <p className="text-sm text-slate-300 mt-1.5">
        Each one gets its own join code. Hand the code out and participants land in that program.
      </p>

      <div className="grid md:grid-cols-2 gap-3 mt-5">
        {programs.length === 0 && (
          <p className="text-sm text-slate-300" data-testid="programs-empty">
            No programs yet. Create your first session, a summer intensive, a class period, a cohort.
          </p>
        )}
        {programs.map((p, i) => (
          <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }} data-testid={`program-card-${p.id}`}
            className="rounded-xl border border-white/10 bg-slate-800/40 p-4">
            <div className="flex items-start gap-2">
              <div className="min-w-0">
                <p className="text-white font-medium truncate">{p.name}</p>
                <p className="text-xs text-slate-300">
                  {(p.program_type || '').replace(/_/g, ' ')}
                  {p.starts_at ? ` · ${p.starts_at.slice(0, 10)}` : ''}
                  {p.ends_at ? ` → ${p.ends_at.slice(0, 10)}` : ''}
                </p>
              </div>
              {canManage && (
                <div className="ml-auto flex items-center gap-2">
                  <button onClick={() => edit(p)} data-testid={`edit-program-${p.id}`}
                    className="text-slate-300 hover:text-white transition-colors">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => archive(p.id)} data-testid={`archive-program-${p.id}`}
                    className="text-slate-300 hover:text-rose-300 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-slate-300">
              <span className="inline-flex items-center gap-1">
                <Users className="w-3 h-3" /> {p.enrolled}{p.capacity ? ` / ${p.capacity}` : ''} enrolled
              </span>
              {(p.age_min || p.age_max) && <span>ages {p.age_min || '-'}-{p.age_max || '-'}</span>}
              {p.location && <span>{p.location}</span>}
              {p.full && <span className="text-amber-300">full</span>}
            </div>
            <button onClick={() => {
              navigator.clipboard?.writeText(p.invite_code);
              toast.success(`Code ${p.invite_code} copied, they join at agenticfuturepath.com/${slug}`);
            }} data-testid={`copy-code-${p.id}`}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 text-xs text-slate-200 hover:border-white/40 transition-colors font-mono">
              <Copy className="w-3 h-3" /> {p.invite_code}
            </button>
          </motion.div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-outfit text-xl">
              {editing ? 'Edit program' : 'New program'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-slate-300 text-sm">Program name</Label>
              <Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })}
                placeholder="e.g. Summer Leadership Intensive 2026" className="input-dark"
                data-testid="program-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Type</Label>
              <Select value={f.program_type} onValueChange={v => setF({ ...f, program_type: v })}>
                <SelectTrigger className="input-dark" data-testid="program-type"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {types.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Join code</Label>
              <Input value={f.invite_code} onChange={e => setF({ ...f, invite_code: e.target.value.toUpperCase() })}
                placeholder="generated for you" className="input-dark font-mono"
                data-testid="program-code" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Starts</Label>
              <Input type="date" value={f.starts_at} onChange={e => setF({ ...f, starts_at: e.target.value })}
                className="input-dark" data-testid="program-starts" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Ends</Label>
              <Input type="date" value={f.ends_at} onChange={e => setF({ ...f, ends_at: e.target.value })}
                className="input-dark" data-testid="program-ends" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Ages from</Label>
              <Input type="number" value={f.age_min} onChange={e => setF({ ...f, age_min: e.target.value })}
                className="input-dark" data-testid="program-age-min" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Ages to</Label>
              <Input type="number" value={f.age_max} onChange={e => setF({ ...f, age_max: e.target.value })}
                className="input-dark" data-testid="program-age-max" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Places in this program</Label>
              <Input type="number" value={f.capacity} onChange={e => setF({ ...f, capacity: e.target.value })}
                className="input-dark" data-testid="program-capacity" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">Where it happens</Label>
              <Input value={f.location} onChange={e => setF({ ...f, location: e.target.value })}
                className="input-dark" data-testid="program-location" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-slate-300 text-sm">Description</Label>
              <Textarea value={f.description} onChange={e => setF({ ...f, description: e.target.value })}
                className="input-dark min-h-[70px]" data-testid="program-description" />
            </div>
          </div>
          <Button onClick={save} disabled={saving || !f.name.trim()} data-testid="program-submit"
            className="rounded-full text-slate-900 font-semibold mt-2" style={{ background: accent }}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Create program'}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramsPanel;
